import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Box, Avatar, Typography, IconButton, Tooltip, useTheme, useMediaQuery, CircularProgress, Menu, MenuItem, List, ListItem, ListItemText, ListItemIcon, Collapse, Button } from '@mui/material';
import { ChevronLeft, ChevronRight, HorizontalRuleRounded as HorizontalRuleRoundedIcon, ExpandLess, ExpandMore, MoreVert, Delete, PictureAsPdf } from '@mui/icons-material';
import keaiLogo from '../../assets/keai_violet.png';
import SettingsModal from './SettingsModal';

import jsPDF from "jspdf";
import useLeftSecEvents from '../hooks/useLeftSecEvents';
import useFileUpload from '../hooks/useFileUpload';
import { pdfjs } from 'react-pdf';
import { RootState } from '../../store/store';
import store from '../../store/store';
import { useAuth0 } from '@auth0/auth0-react';

import { alpha } from '@mui/material/styles';
import { clearChat } from '../../store/slices/chatHistorySlice';
import useWebSocketContext from '../../hooks/useWebSocketContext';
import { useNavigate } from 'react-router-dom';
import useChatNavigation from '../../hooks/useChatNavigation';
import ChatNavigationService from '../../services/ChatNavigationService';
import { resetChatSession } from '../../store/slices/chatSlice';
import { useTranslation } from '../../hooks/useTranslation';
  
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface LeftSectionProps {
  sx?: object;
  isOpen: boolean;
  onToggleSection: () => void;
}

interface ChatHistory {
  id: string;
  title: string;
  date: string;
  timestamp: Date;
}

const LeftSection: React.FC<LeftSectionProps> = ({ sx, isOpen, onToggleSection }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Responsive breakpoints for mobile enhancements
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 960px
  const { logout: auth0Logout, user: auth0User } = useAuth0();
  const {
    isHovered,
    setIsHovered,
    isLogoHovered,
    setIsLogoHovered,
    isClicked,
    handleResetChat,
  } = useLeftSecEvents();
  
  const { handleFileDrop, handleFileSelect, handleDragEnter, handleDragLeave, handleDragOver } = useFileUpload();
  
  // Use RootState as a generic argument to useSelector to get the status from kbSlice
  const status = useSelector((state: RootState) => state.kb.status);
  const kbError = useSelector((state: RootState) => state.kb.error);
  
  // Phase 4: Get real chat history from Redux store
  const chatGroups = useSelector((state: RootState) => state.chatHistory.chatGroups);
  const activeChatGroup = useSelector((state: RootState) => state.chatHistory.activeChatGroup);
  const isChatHistoryLoading = useSelector((state: RootState) => state.chatHistory.isLoading);
  
  // Phase 4: WebSocket context for loading chat history
  const { sendToWs, isConnected } = useWebSocketContext();
  
  // Get background mode for conditional styling
  const backgroundMode = useSelector((state: RootState) => state.background.mode);
  
  // Phase 4: Helper function to format relative time
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return date.toLocaleDateString();
  };

  // Phase 4: Convert ChatGroup to ChatHistory format for compatibility
  const chatHistory = chatGroups.map(group => ({
    id: group.id,
    title: group.title,
    date: formatRelativeTime(group.updated_at),
    timestamp: new Date(group.updated_at)
  }));
  
  // Phase 3: Collapsible sections state for mobile space management
  const [expandedSections, setExpandedSections] = useState({
    today: true,  // Always expand today's chats
    yesterday: true,  // Start expanded
    lastWeek: false,  // Start collapsed
    older: false,  // Start collapsed
  });

  // Phase 4: Loading and optimization states
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showAllItems, setShowAllItems] = useState(!isMobile); // Show limited items on mobile initially
  const [isPullToRefreshing, setIsPullToRefreshing] = useState(false);
  const [lastTap, setLastTap] = useState(0); // For double-tap detection

  // Phase 4: Mobile performance optimization constants
  const MOBILE_INITIAL_LIMIT = 5; // Show only 5 most recent items initially on mobile
  const ITEMS_PER_LOAD = 10; // Load 10 more items when "Show More" is tapped

  // Phase 4: Content optimization functions
  const getFilteredChatHistory = () => {
    if (!isMobile || showAllItems) {
      return chatHistory; // Show all items on desktop or when explicitly requested
    }
    // On mobile, initially show limited items for performance
    return chatHistory.slice(0, MOBILE_INITIAL_LIMIT);
  };

  const hasMoreItems = () => {
    return isMobile && !showAllItems && chatHistory.length > MOBILE_INITIAL_LIMIT;
  };

  const handleShowMore = () => {
    setIsLoadingHistory(true);
    // Simulate loading delay for better UX feedback
    setTimeout(() => {
      setShowAllItems(true);
      setIsLoadingHistory(false);
    }, 300);
  };

  // Phase 4: Mobile shortcuts - Pull to refresh functionality
  const handlePullToRefresh = () => {
    if (!isMobile || isPullToRefreshing || !isConnected) return;
    setIsPullToRefreshing(true);
    
    // Refresh chat history from backend
    const request = JSON.stringify({
      method: 'get_chat_groups',
      params: {}
    });
    sendToWs(request);
    
    // Stop loading indicator after delay
    setTimeout(() => {
      setIsPullToRefreshing(false);
    }, 800);
  };

  // Phase 4: Mobile shortcuts - Double tap to open user menu
  const handleLogoDoubleTouch = (event: React.TouchEvent) => {
    if (!isMobile) return;
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 500 && tapLength > 0) {
      // Double tap detected
      handleOpenSettings(); // Open settings modal instead of user menu
    }
    setLastTap(currentTime);
  };
  
  useEffect(() => {
    console.log(`Status changed to: ${status}`);
  }, [status]);

  // Phase 4: Performance monitoring for mobile
  useEffect(() => {
    if (isMobile) {
      const startTime = performance.now();
      return () => {
        const endTime = performance.now();
        console.log(`LeftSection mobile render time: ${endTime - startTime}ms`);
      };
    }
  }, [isMobile, chatHistory.length, showAllItems]);

  // Phase 4: Smart keyboard handling for mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const handleKeyboard = () => {
      // Auto-close drawer when virtual keyboard appears on mobile
      if (window.visualViewport && window.visualViewport.height < window.innerHeight * 0.75) {
        if (isOpen) {
          onToggleSection();
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleKeyboard);
      return () => window.visualViewport?.removeEventListener('resize', handleKeyboard);
    }
  }, [isMobile, isOpen, onToggleSection]);

  // Phase 4: User initialization is now handled automatically in WebSocketContext
  // No need for manual chat history loading here

  const user_name = useSelector((state: RootState) => state.session.user_name);
  
  const userInitial = user_name ? user_name.charAt(0).toUpperCase() : '';

  const firstName = user_name ? user_name.trim().split(/\s+/)[0] : '';


  const handleNewChat = () => {
    dispatch(resetChatSession());
    dispatch(clearChat());
    navigate('/');
  };

  const { navigateToChat } = useChatNavigation();
  const { t } = useTranslation();

  const handleSelectChat = (chatId: string) => {
    navigateToChat(chatId);
    if (isMobile) {
      onToggleSection();
    }
  };

  // Phase 3: Toggle section expansion for mobile
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Phase 4: Group chat history by time periods using filtered content
  const groupChatsByTime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    
    const filteredHistory = getFilteredChatHistory(); // Use optimized content for mobile
    
    const todayChats = filteredHistory.filter(chat => chat.timestamp >= today);
    const yesterdayChats = filteredHistory.filter(chat => chat.timestamp >= yesterday && chat.timestamp < today);
    const lastWeekChats = filteredHistory.filter(chat => chat.timestamp >= lastWeekStart && chat.timestamp < yesterday);
    const olderChats = filteredHistory.filter(chat => chat.timestamp < lastWeekStart);
    
    return {
      today: todayChats,
      yesterday: yesterdayChats,
      lastWeek: lastWeekChats,
      older: olderChats
    };
  };

  const groupedChats = groupChatsByTime();

  // Phase 1 & 2: Responsive calculations
  const drawerWidth = isMobile ? '70%' : isTablet ? '45%' : '15%';
  const toggleButtonLeft = isMobile 
    ? (isOpen ? '67%' : '-10px')  // Adjusted to match new drawer width of 70%
    : isTablet 
      ? (isOpen ? '42%' : '-10px') 
      : (isOpen ? '14.5%' : '-10px');

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>('');
  
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, chatId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedChatId(chatId);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedChatId(null);
  };

  const handleDeleteChat = () => {
    if (selectedChatId) {
      const request = {
        method: 'delete_chat_group',
        params: { chat_group_id: selectedChatId }
      };
      sendToWs(JSON.stringify(request));
      handleMenuClose();
      handleNewChat();
    }
  };

  //Getting msg for export in PDF 
  const activeChatGroupId = useSelector((state: RootState) => state.chatHistory.activeChatGroup);
  const chatGroups2 = useSelector((state: RootState) => state.chatHistory.chatGroups);

  // Helper function to load chat messages efficiently
  const loadChatMessages = async (chatId: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      // Check if messages are already loaded
      let selectedGroup = store.getState().chatHistory.chatGroups.find((group: any) => group.id === chatId);
      
      if (selectedGroup && selectedGroup.messages && selectedGroup.messages.length > 0) {
        console.log('✅ Messages already loaded, using cached data');
        resolve(selectedGroup);
        return;
      }

      console.log('🔄 Loading messages for chat:', chatId);
      
      // Set up a timeout to prevent infinite waiting
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for messages to load'));
      }, 10000); // 10 second timeout

      // Set up a listener for state changes
      let attempts = 0;
      const maxAttempts = 20; // Check every 250ms for up to 5 seconds
      
      const checkForMessages = () => {
        attempts++;
        const currentState = store.getState();
        const group = currentState.chatHistory.chatGroups.find((g: any) => g.id === chatId);
        
        if (group && group.messages && group.messages.length > 0) {
          clearTimeout(timeout);
          console.log('✅ Messages loaded successfully');
          resolve(group);
          return;
        }
        
        if (attempts < maxAttempts) {
          setTimeout(checkForMessages, 250);
        } else {
          clearTimeout(timeout);
          // Return the group even if no messages (might be an empty chat)
          resolve(group || null);
        }
      };

      // Send the WebSocket request
      try {
        const request = JSON.stringify({
          method: 'get_chat_messages',
          params: { chat_group_id: chatId }
        });
        sendToWs(request);
        console.log('📡 Sent get_chat_messages request');
        
        // Start checking for messages after a brief delay
        setTimeout(checkForMessages, 500);
      } catch (error) {
        clearTimeout(timeout);
        console.error('❌ Failed to send WebSocket request:', error);
        reject(error);
      }
    });
  };

  // Function to export chat history as PDF
  const handleExportPdf = async () => {
    if (!selectedChatId) {
      console.error("No chat selected for export");
      return;
    }

    setIsExporting(true);
    setExportProgress('Loading messages...');
    
    try {
      console.log('🚀 Starting optimized export process for chat:', selectedChatId);

      // Step 1: Efficiently load chat messages
      let selectedGroup: any;
      try {
        selectedGroup = await loadChatMessages(selectedChatId);
      } catch (error) {
        console.error('❌ Failed to load messages:', error);
        setExportProgress('Loading available data...');
        // Try to get whatever data is available
        selectedGroup = chatGroups2.find(group => group.id === selectedChatId);
      }

      // Step 2: Verify we have a valid chat group
      if (!selectedGroup) {
        console.error('❌ No chat group found for export');
        setIsExporting(false);
        setExportProgress('');
        return;
      }

      setExportProgress('Generating PDF...');

      // Step 3: Now proceed with optimized PDF generation
      console.log(`🎯 Starting optimized PDF generation for chat: ${selectedGroup.title}`);
      console.log(`📊 Messages to export: ${selectedGroup.messages?.length || 0}`);
      
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Optimized text rendering - set font once and reuse
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      
      // Simple text function
      const addText = (text: string, x: number, y: number, fontSize: number = 12, fontStyle: string = 'normal', color: number[] = [0, 0, 0]) => {
        doc.setFont('helvetica', fontStyle);
        doc.setFontSize(fontSize);
        doc.setTextColor(color[0], color[1], color[2]);
        doc.text(text, x, y);
      };
      
      // Optimized PDF Header Design
      addText(`Title: ${selectedGroup.title}`, 15, 25, 18, 'bold', [51, 51, 153]);
      
      // Decorative line under title
      doc.setDrawColor(51, 51, 153);
      doc.setLineWidth(0.8);
      doc.line(15, 30, 195, 30);
      
      // Timestamp and document info
      const timestamp = new Date().toLocaleString();
      addText(`Generated: ${timestamp}`, 15, 38, 9, 'normal', [100, 100, 100]);
      addText(`Messages: ${selectedGroup.messages?.length || 0}`, 15, 45, 9, 'normal', [100, 100, 100]);
      
      let y = 60;

      // Simple message processing that works
      if (selectedGroup.messages && selectedGroup.messages.length > 0) {
        console.log(`📝 Processing ${selectedGroup.messages.length} messages for PDF`);
        
        selectedGroup.messages.forEach((msg: any, idx: number) => {
          // Determine sender and colors
          const isUser = msg.source === "user";
          const sender = isUser ? "You" : "AI";
          const senderColor = isUser ? [153, 51, 102] : [51, 102, 153];
          const messageColor = [51, 51, 51];
          
          const text = msg.content || "";
          
          // Check for new page
          if (y > 260) {
            doc.addPage();
            y = 25;
          }
          
          // Add sender label
          addText(`${sender}:`, 15, y, 13, 'bold', senderColor);
          
          if (text.trim()) {
            // Set font for message text measurement
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(12);
            
            // Calculate sender width with proper font
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            const senderWidth = doc.getTextWidth(`${sender}: `);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(12);
            
            // Conservative page dimensions (A4 = 210mm width)
            const pageWidth = 210;
            const leftMargin = 15;
            const rightMargin = 20; // Increased right margin for safety
            const usableWidth = pageWidth - leftMargin - rightMargin; // 175mm
            
            // Calculate max widths with extra safety buffer
            const firstLineMaxWidth = Math.max(usableWidth - senderWidth - 10, 60); // At least 60mm
            const continuationMaxWidth = Math.max(usableWidth - 10, 100); // At least 100mm
            
            // Method 1: Try to fit first part on same line as sender
            let remainingText = text;
            let isFirstLine = true;
            
            while (remainingText.trim()) {
              const maxWidth = isFirstLine ? firstLineMaxWidth : continuationMaxWidth;
              const wrappedLines = doc.splitTextToSize(remainingText, maxWidth);
              
              if (wrappedLines.length > 0) {
                const currentLine = wrappedLines[0];
                
                if (isFirstLine) {
                  // Place first line after sender
                  addText(currentLine, leftMargin + senderWidth, y, 12, 'normal', messageColor);
                  isFirstLine = false;
                } else {
                  // Place continuation lines at left margin
                  y += 6;
                  if (y > 275) {
                    doc.addPage();
                    y = 25;
                  }
                  addText(currentLine, leftMargin, y, 12, 'normal', messageColor);
                }
                
                // Remove processed text
                const processedLength = currentLine.length;
                remainingText = remainingText.substring(processedLength).trim();
                
                // Safety check to prevent infinite loop
                if (processedLength === 0) {
                  break;
                }
              } else {
                break;
              }
            }
          }
          
          // Add spacing between messages
          y += 8;
        });
      } else {
        console.log('⚠️ No messages found');
        addText("No messages found in this chat.", 15, y, 12, 'italic', [100, 100, 100]);
      }

      // Optimized footer generation
      const pageCount = (doc as any).getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Footer separator line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.line(15, 285, 195, 285);
        
        // Footer text
        addText(`Page ${i} of ${pageCount}`, 15, 290, 8, 'normal', [150, 150, 150]);
        addText(`RegenAI`, 120, 290, 8, 'normal', [150, 150, 150]);
      }

      // Generate filename with timestamp
      const timestamp2 = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `${selectedGroup.title.replace(/[^a-zA-Z0-9\s]/g, '_')}-${timestamp2}.pdf`;
      
      setExportProgress('Saving file...');
      console.log(`✅ PDF Export completed: ${selectedGroup.messages?.length || 0} messages exported`);
      doc.save(filename);
      
      // Close the menu after successful export
      handleMenuClose();
      
    } catch (error) {
      console.error("Error exporting PDF:", error);
      setExportProgress('Export failed');
      // Create a simple error PDF
      const doc = new jsPDF();
      doc.text("Error exporting chat. Please try again.", 10, 10);
      doc.save("export-error.pdf");
    } finally {
      setIsExporting(false);
      setExportProgress('');
    }
  };

  const renderChatItem = (chat: ChatHistory) => (
    <ListItem
      key={`${chat.id}`}
      onClick={() => handleSelectChat(chat.id)}
      sx={{
        borderRadius: '8px',
        mb: 0.25,
        padding: '4px 12px',
        cursor: 'pointer',
        backgroundColor: activeChatGroup === chat.id 
          ? theme.palette.primary.main
          : 'transparent',
        '&:hover': {
          backgroundColor: activeChatGroup !== chat.id 
            ? alpha(theme.palette.primary.main, 0.3)
            : theme.palette.primary.main,
          '& .menu-button': {
            opacity: 1,
          },
        },
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <ListItemText 
        primary={chat.title}
        primaryTypographyProps={{
          fontSize: isMobile ? '0.875rem' : '0.8rem',
          fontWeight: 400,
          color: backgroundMode === "default" 
            ? '#ffffff'
            : (theme.palette.mode === "dark" ? theme.palette.text.secondary : theme.palette.text.disabled),
          noWrap: true,
          flex: 1,
          sx: {
            lineHeight: '1.2',
            width: '110%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            paddingY: '2px',
          }
        }}
      />
      <IconButton
        className="menu-button"
        size="small"
        onClick={(e) => handleMenuClick(e, chat.id)}
        sx={{
          opacity: isMobile ? 1 : 0,
          transition: 'opacity 0.2s',
          color: backgroundMode === "default" 
            ? '#ffffff'
            : theme.palette.text.secondary,
          padding: '4px',
          marginLeft: '8px',
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
    </ListItem>
  );

  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleCloseSettings = () => setSettingsOpen(false);

  const handleOpenSettings = () => setSettingsOpen(true);

  return (
    <>
      <Tooltip
        title="Toggle left section"
        sx={{
          ".MuiTooltip-tooltip": {
            backgroundColor: 'rgb(245, 245, 245)',
          }
        }}
      >
        <IconButton
          onClick={onToggleSection}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label={isOpen ? 'Close section' : 'Open section'}
          sx={{
            position: 'absolute',
            left: toggleButtonLeft,
            top: '70%',
            transform: 'translateY(-50%)',
            backgroundColor: 'transparent',
            color: 'rgb(138, 43, 226)',
            width: isMobile ? '48px' : '40px', // Larger touch target on mobile
            height: isMobile ? '48px' : '40px',
            '&:hover': {
              color: 'rgb(138, 43, 226)',
              fontWeight: 900,
              boxShadow: 'none',
            },
            transition: 'left 0.8s ease',
            zIndex: 1300,
            fontSize: isMobile ? '1.2rem' : '1.0rem', // Larger icons on mobile
          }}
        >
          {isOpen ? (
            <>
              <HorizontalRuleRoundedIcon sx={{ transform: 'rotate(90deg)', opacity: isHovered ? 0 : 1, transition: 'opacity 0.3s' }} />
              <ChevronLeft sx={{ position: 'absolute', opacity: isHovered ? 1 : 0, transition: 'opacity 0.3s' }} />
            </>
          ) : (
            <>
              <HorizontalRuleRoundedIcon sx={{ transform: 'rotate(90deg)', opacity: isHovered ? 0 : 1, transition: 'opacity 0.3s' }} />
              <ChevronRight sx={{ position: 'absolute', opacity: isHovered ? 1 : 0, transition: 'opacity 0.3s' }} />
            </>
          )}
        </IconButton>
      </Tooltip>

      <Drawer
        sx={{
          width: isOpen ? drawerWidth : '0%',
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isOpen ? drawerWidth : '0%',
            boxSizing: 'border-box',
            backgroundColor: "transparent",
            borderRight: 'none',
            transition: 'width 0.8s ease, transform 0.8s ease !important',
          },
          transition: 'width 0.8s ease !important',
          ...sx,
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={isOpen}
        onClose={isMobile ? onToggleSection : undefined}
        transitionDuration={800}
      >
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
        >
          {/* Logo with New Chat Functionality */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding={2}
            marginTop="10px"
          >
            <Tooltip title={t('newChat')}>
              <Box
                onClick={handleNewChat}
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                }}
              >
                <img
                  src={keaiLogo}
                  alt="Keai Logo"
                  style={{
                    width: '35px',
                    height: '35px',
                  }}
                />
              </Box>
            </Tooltip>
          </Box>

          {/* Chat History Section */}
          <Box sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            padding: '0 16px',
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.primary.main, 0.2),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.primary.main, 0.5)
                : alpha(theme.palette.primary.main, 0.4),
            },
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(theme.palette.primary.main, 0.3)} transparent`,
          }}>
            {/* Phase 4: Pull to Refresh Indicator for Mobile */}
            {isMobile && isPullToRefreshing && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                padding: '16px',
                color: theme.palette.primary.main,
              }}>
                <CircularProgress size={20} sx={{ marginRight: '8px' }} />
                <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
                  Refreshing...
                </Typography>
              </Box>
            )}

            {/* Chat History Sections */}
            {getFilteredChatHistory().length > 0 && (
              <>
                {/* Today Section - Only show if there are chats */}
                {groupedChats.today.length > 0 && (
                  <>
                    <Box 
                      onClick={() => toggleSection('today')}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '8px 4px 4px',
                        '&:hover': {
                          borderRadius: '4px',
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : alpha(theme.palette.text.secondary, 0.8),
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        Today
                      </Typography>
                      <IconButton 
                        size="small" 
                        sx={{ 
                          padding: '2px', 
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : theme.palette.text.secondary 
                        }}
                      >
                        {expandedSections.today ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </Box>
                    <Collapse in={expandedSections.today} timeout="auto" unmountOnExit>
                      <List sx={{ padding: 0 }}>
                        {groupedChats.today.map(chat => renderChatItem(chat))}
                      </List>
                    </Collapse>
                  </>
                )}
                
                {/* Yesterday Section - Only show if there are chats */}
                {groupedChats.yesterday.length > 0 && (
                  <>
                    <Box 
                      onClick={() => toggleSection('yesterday')}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '8px 4px 4px',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          borderRadius: '4px',
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : alpha(theme.palette.text.secondary, 0.8),
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        Yesterday
                      </Typography>
                      <IconButton 
                        size="small" 
                        sx={{ 
                          padding: '2px', 
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : theme.palette.text.secondary 
                        }}
                      >
                        {expandedSections.yesterday ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </Box>
                    <Collapse in={expandedSections.yesterday} timeout="auto" unmountOnExit>
                      <List sx={{ padding: 0 }}>
                        {groupedChats.yesterday.map(chat => renderChatItem(chat))}
                      </List>
                    </Collapse>
                  </>
                )}
                
                {/* Last 7 Days Section - Only show if there are chats */}
                {groupedChats.lastWeek.length > 0 && (
                  <>
                    <Box 
                      onClick={() => toggleSection('lastWeek')}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '8px 4px 4px',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          borderRadius: '4px',
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : alpha(theme.palette.text.secondary, 0.8),
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        Last 7 days
                      </Typography>
                      <IconButton 
                        size="small" 
                        sx={{ 
                          padding: '2px', 
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : theme.palette.text.secondary 
                        }}
                      >
                        {expandedSections.lastWeek ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </Box>
                    <Collapse in={expandedSections.lastWeek} timeout="auto" unmountOnExit>
                      <List sx={{ padding: 0 }}>
                        {groupedChats.lastWeek.map(chat => renderChatItem(chat))}
                      </List>
                    </Collapse>
                  </>
                )}
                
                {/* Older Section - Only show if there are chats */}
                {groupedChats.older.length > 0 && (
                  <>
                    <Box 
                      onClick={() => toggleSection('older')}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '8px 4px 4px',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          borderRadius: '4px',
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : theme.palette.text.secondary,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        Older
                      </Typography>
                      <IconButton 
                        size="small" 
                        sx={{ 
                          padding: '2px', 
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : theme.palette.text.secondary 
                        }}
                      >
                        {expandedSections.older ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </Box>
                    <Collapse in={expandedSections.older} timeout="auto" unmountOnExit>
                      <List sx={{ padding: 0 }}>
                        {groupedChats.older.map(chat => renderChatItem(chat))}
                      </List>
                    </Collapse>
                  </>
                )}
              </>
            )}

            {/* Phase 4: Show More Button for Mobile */}
            {hasMoreItems() && (
              <Box sx={{ 
                padding: '12px 8px', 
                textAlign: 'center',
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                marginTop: '8px'
              }}>
                <Button
                  onClick={handleShowMore}
                  disabled={isLoadingHistory}
                  startIcon={isLoadingHistory ? <CircularProgress size={16} /> : undefined}
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                    minHeight: '44px', // Touch-friendly height
                    width: '100%',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                    '&:disabled': {
                      color: theme.palette.text.disabled,
                    }
                  }}
                >
                  {isLoadingHistory ? 'Loading...' : `Show More (${chatHistory.length - MOBILE_INITIAL_LIMIT} remaining)`}
                </Button>
              </Box>
            )}
          </Box>

          {/* File Upload Section */}
          

          {/* User Profile Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '15px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderRadius: '8px',
              },
              transition: 'background-color 0.3s, transform 0.2s',
            }}
            onClick={handleOpenSettings}
            onMouseEnter={() => setIsLogoHovered(true)}
            onMouseLeave={() => setIsLogoHovered(false)}
            onTouchEnd={isMobile ? handleLogoDoubleTouch : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', paddingY: 1, paddingX: 2, gap: 1 }}>
              {auth0User?.picture ? (
                <Avatar
                  alt="User Logo"
                  src={auth0User.picture}
                  sx={{
                    width: 28,
                    height: 28,
                    transform: isLogoHovered ? 'scale(1.1)' : 'scale(1.0)',
                    bgcolor: '#2E7D32',
                    color: 'white',
                    transition: 'transform 0.2s',
                  }}
                >{userInitial}</Avatar>
              ) : (
                <Avatar
                  alt="User Logo"
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: '#2E7D32',
                    transform: isLogoHovered ? 'scale(1.1)' : 'scale(1.0)',
                    transition: 'transform 0.2s',
                  }}
                >
                  {auth0User?.name ? auth0User.name.charAt(0).toUpperCase() : userInitial}
                </Avatar>
              )}
              {firstName && firstName !== '' && (
                <Typography
                  variant="subtitle1"
                sx={{
                  marginLeft: 1,
                  color: 'gray',
                  textTransform: 'none',
                  transform: isLogoHovered ? 'scale(1.1)' : 'scale(1.0)',
                  transition: 'transform 0.2s',
                }}
              >
                {firstName}
              </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Settings Modal */}
      <SettingsModal 
        open={settingsOpen}
        onClose={handleCloseSettings}
      />

      {/* Three-dot menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem 
          onClick={handleExportPdf} 
          disabled={isExporting}
          sx={{ 
            fontSize: isMobile ? '0.875rem' : '0.8rem',
            padding: '10px 12px',
            minHeight: '32px',
          }}
        >
          <ListItemIcon>
            {isExporting ? (
              <CircularProgress size={16} sx={{ color: theme.palette.text.secondary }} />
            ) : (
              <PictureAsPdf fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            )}
          </ListItemIcon>
          <ListItemText 
            primary={isExporting ? (exportProgress || "Exporting...") : "Export as PDF"}
            primaryTypographyProps={{
              fontSize: 'inherit',
              fontWeight: 400,
              color: theme.palette.text.secondary
            }}
          />
          
        </MenuItem>
        <MenuItem 
          onClick={handleDeleteChat} 
          sx={{ 
            fontSize: isMobile ? '0.875rem' : '0.8rem',
            padding: '4px 12px',
            minHeight: '32px',
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          </ListItemIcon>
          <ListItemText 
            primary="Delete chat"
            primaryTypographyProps={{
              fontSize: 'inherit',
              fontWeight: 400,
              color: theme.palette.text.secondary
            }}
          />
          
        </MenuItem>
        
      </Menu>
    </>
  );
};

export default LeftSection;